import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import {
    MapPin,
    Users,
    ArrowRight,
    Building2,
    CheckCircle2,
    Star,
} from "lucide-react";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "../components/ui/accordion";
import { Button } from "../components/ui/button";
import Seo from "../lib/Seo";
import CenterCard from "../components/CenterCard";
import Breadcrumbs from "../components/Breadcrumbs";
import { fetchCenters } from "../lib/api";
import { getCity, SEO_CITIES, CITY_SLUGS } from "../lib/seoPages";

export default function CityDetail() {
    const { slug } = useParams();
    const city = getCity(slug);

    const [centers, setCenters] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!city) return;
        const controller = new AbortController();
        let cancelled = false;
        setLoading(true);
        fetchCenters(
            { city: city.name, page: 1, limit: 12 },
            { signal: controller.signal },
        )
            .then((data) => {
                if (cancelled) return;
                setCenters(data.centers || []);
                setTotal(data.total || 0);
            })
            .catch(() => {
                if (cancelled) return;
                setCenters([]);
                setTotal(0);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
            controller.abort();
        };
    }, [city]);

    if (!city) return <Navigate to="/centros" replace />;

    const faqJsonLd = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: (city.faq || []).map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
    };

    const related = CITY_SLUGS.filter((s) => s !== slug)
        .slice(0, 4)
        .map((s) => SEO_CITIES[s]);

    return (
        <>
            <Seo
                title={city.metaTitle || city.h1}
                description={city.metaDescription}
                path={`/ciudades/${city.slug}`}
                keywords={`centros de rehabilitación de drogas en ${city.name}, rehabilitación ${city.name}, tratamiento drogas ${city.name}, clínica desintoxicación ${city.name}`}
                jsonLd={faqJsonLd}
            />

            <section className="border-b border-slate-800 bg-slate-950">
                <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
                    <Breadcrumbs
                        items={[
                            { name: "Inicio", path: "/" },
                            { name: "Regiones", path: "/regiones" },
                            { name: city.region, path: `/regiones/${city.regionSlug}` },
                            { name: city.name },
                        ]}
                    />
                    <div className="mt-5 inline-flex items-center gap-2 rounded-full border border-cyan-500/30 bg-cyan-500/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-cyan-300">
                        <MapPin className="h-3.5 w-3.5" />
                        {city.isCapital ? "Capital · " : "Ciudad · "} Región {city.region}
                    </div>
                    <h1
                        data-testid="city-h1"
                        className="mt-4 text-3xl font-extrabold leading-tight tracking-tight text-slate-50 sm:text-5xl"
                    >
                        {city.h1}
                    </h1>
                    <p className="mt-5 max-w-3xl text-base leading-relaxed text-slate-400 sm:text-lg">
                        {city.intro}
                    </p>
                    <div className="mt-6 flex flex-wrap items-center gap-4 text-sm">
                        <div className="inline-flex items-center gap-2 rounded-md border border-slate-800 bg-slate-900 px-3 py-2">
                            <Building2 className="h-4 w-4 text-cyan-400" />
                            <span className="text-slate-200">
                                <strong data-testid="city-center-count">
                                    {loading ? "—" : total}
                                </strong>{" "}
                                centros disponibles
                            </span>
                        </div>
                        {city.population ? (
                            <div className="inline-flex items-center gap-2 rounded-md border border-slate-800 bg-slate-900 px-3 py-2">
                                <Users className="h-4 w-4 text-cyan-400" />
                                <span className="text-slate-200">{city.population}</span>
                            </div>
                        ) : null}
                    </div>
                </div>
            </section>

            {/* Contexto */}
            <section className="border-b border-slate-800 py-14">
                <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
                    <h2 className="text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl">
                        Contexto del consumo y oferta clínica en {city.name}
                    </h2>
                    <p className="mt-4 leading-relaxed text-slate-300">
                        {city.contexto}
                    </p>
                </div>
            </section>

            {/* Centros */}
            <section className="border-b border-slate-800 py-14">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-wrap items-end justify-between gap-4">
                        <div>
                            <div className="inline-flex items-center gap-2 rounded-full border border-cyan-500/30 bg-cyan-500/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-cyan-300">
                                <Star className="h-3.5 w-3.5 fill-cyan-400 text-cyan-400" />
                                Directorio local
                            </div>
                            <h2 className="mt-4 text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl">
                                Centros de rehabilitación en {city.name}
                            </h2>
                            <p className="mt-2 text-slate-400">
                                {loading
                                    ? "Cargando centros..."
                                    : total > 0
                                      ? `${total} centro${total === 1 ? "" : "s"} verificados con distintos enfoques clínicos.`
                                      : `Aún no tenemos centros directamente registrados en ${city.name}. Revisa la región ${city.region}.`}
                            </p>
                        </div>
                        <Link
                            to={`/centros?city=${encodeURIComponent(city.name)}`}
                            rel="nofollow"
                            data-testid="city-see-all"
                            className="group inline-flex items-center gap-1 rounded-md border border-slate-800 bg-slate-900 px-4 py-2 text-sm font-medium text-cyan-400 hover:border-cyan-500/40 hover:text-cyan-300"
                        >
                            Ver con filtros
                            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                        </Link>
                    </div>

                    <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                        {loading ? (
                            Array.from({ length: 6 }).map((_, i) => (
                                <div
                                    key={i}
                                    className="h-80 animate-pulse rounded-xl border border-slate-800 bg-slate-900"
                                />
                            ))
                        ) : centers.length > 0 ? (
                            centers.map((c) => <CenterCard key={c.center_id} center={c} />)
                        ) : (
                            <div className="col-span-full rounded-xl border border-dashed border-slate-800 bg-slate-900 p-10 text-center">
                                <p className="text-slate-300">
                                    Explora opciones cercanas o el directorio nacional.
                                </p>
                                <Link to="/centros" className="mt-4 inline-block">
                                    <Button className="bg-cyan-500 text-slate-950 hover:bg-cyan-400">
                                        Ver directorio completo
                                    </Button>
                                </Link>
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* Cómo elegir */}
            <section className="border-b border-slate-800 py-14">
                <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
                    <h2 className="text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl">
                        Cómo elegir un centro de rehabilitación en {city.name}
                    </h2>
                    <p className="mt-4 leading-relaxed text-slate-300">
                        {city.comoElegir}
                    </p>
                    <ul className="mt-6 space-y-3">
                        {[
                            "Acreditación de la SEREMI de Salud y registro SENDA si corresponde.",
                            "Equipo multidisciplinario con médico/psiquiatra, psicólogo y terapeuta en adicciones.",
                            "Programa con evaluación inicial, tratamiento estructurado y seguimiento post-alta.",
                            "Presupuesto escrito transparente, especificando qué incluye y qué queda fuera.",
                            "Política clara de visitas familiares y prohibición de tratos degradantes.",
                        ].map((txt, i) => (
                            <li
                                key={i}
                                className="flex gap-3 rounded-xl border border-slate-800 bg-slate-900 p-4"
                            >
                                <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-cyan-400" />
                                <span className="text-slate-300">{txt}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            {/* FAQ */}
            <section className="border-b border-slate-800 py-14">
                <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
                    <div className="inline-flex items-center gap-2 rounded-full border border-cyan-500/30 bg-cyan-500/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-cyan-300">
                        Preguntas frecuentes
                    </div>
                    <h2 className="mt-4 text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl">
                        Dudas frecuentes sobre rehabilitación en {city.name}
                    </h2>
                    <Accordion
                        type="single"
                        collapsible
                        className="mt-6 rounded-xl border border-slate-800 bg-slate-900"
                        data-testid="city-faq"
                    >
                        {(city.faq || []).map((f, i) => (
                            <AccordionItem
                                key={i}
                                value={`c-faq-${i}`}
                                className="border-slate-800 px-5"
                            >
                                <AccordionTrigger className="text-left text-slate-100 hover:text-cyan-400">
                                    {f.q}
                                </AccordionTrigger>
                                <AccordionContent className="text-slate-400">
                                    {f.a}
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </div>
            </section>

            {/* Otras ciudades */}
            <section className="py-14">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <h2 className="text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl">
                        Otras ciudades en Chile
                    </h2>
                    <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        {related.map((r) => (
                            <Link
                                key={r.slug}
                                to={`/ciudades/${r.slug}`}
                                data-testid={`related-city-${r.slug}`}
                                className="card-hover rounded-xl border border-slate-800 bg-slate-900 p-5"
                            >
                                <div className="flex items-center gap-2 text-cyan-400">
                                    <MapPin className="h-4 w-4" />
                                    <span className="text-xs font-medium uppercase tracking-wider">
                                        Ciudad
                                    </span>
                                </div>
                                <h3 className="mt-3 text-lg font-semibold text-slate-50">
                                    {r.name}
                                </h3>
                                <p className="mt-2 text-xs text-slate-400">
                                    Región {r.region}
                                </p>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>
        </>
    );
}
